import m from 'mithril';
import { formatRupiah, getMithril, postMithril, putMithril, deleteMithril, getUserData, getJwtToken } from '../../utils.js';
import { getTerm, nomenclatureStream } from '../../js/nomenclature.js';
import toast, { showConfirmation } from '../../js/toaster.js';

// Debug helper to check if component is properly mounted
const debugComponentMount = (componentName, event) => {
    console.log(`${componentName}: ${event}`, {
        componentMounted: typeof window !== 'undefined',
        documentReady: document.readyState === 'complete',
        timestamp: new Date().toISOString()
    });
};

// Toast wrapper to ensure it works
const showErrorToast = (message, duration = 5000) => {
    console.log('Showing toast with message:', message);
    try {
        if (typeof toast.show === 'function') {
            toast.show(message, 'error', duration);
            console.log('Toast shown successfully via toast.show');
        } else if (typeof toast.error === 'function') {
            toast.error(message, duration);
            console.log('Toast shown successfully via toast.error');
        } else {
            console.error('Toast function not available:', toast);
        }
    } catch (error) {
        console.error('Error showing toast:', error);
    }
};

// Simpanan Tab Component
const SimpananTab = {
    oninit: function (vnode) {
        debugComponentMount('SimpananTab', 'oninit started');
        this.savingProducts = [];
        this.isLoading = true;
        this.isFormVisible = false;
        this.isEditFormVisible = false;
        this.editingProduct = null;
        this.subunits = [];
        this.subunitsLoading = true;
        this.subunitsLoading = true;

        // Subscribe to nomenclature changes
        this.nomenclatureUnsubscribe = nomenclatureStream.map(() => {
            m.redraw();
        });

        // Load initial data
        this.loadData();

        // Load subunits (now synchronous, uses cached data)
        this.loadSubunits();

        debugComponentMount('SimpananTab', 'oninit completed');
    },

    oncreate: function (vnode) {
        debugComponentMount('SimpananTab', 'oncreate - DOM mounted');
        // Ensure event handlers are properly attached after DOM is mounted
        this.ensureEventHandlers();
    },

    onunload: function (vnode) {
        // Unsubscribe from nomenclature stream to prevent memory leaks
        if (this.nomenclatureUnsubscribe) {
            this.nomenclatureUnsubscribe();
        }
    },

    ensureEventHandlers: function () {
        console.log('SimpananTab: Ensuring event handlers are attached');
        // This method can be used to re-attach event handlers if needed
    },

    loadData: async function () {
        try {
            const response = await getMithril('/api/saving-products', {
                component: this,
                showLoading: true,
                showErrorToast: true // Let makeMithrilRequest handle errors automatically
            });

            if (response.success) {
                this.savingProducts = response.data || [];
            } else {
                // This shouldn't happen as errors are handled by makeMithrilRequest
                console.error('Unexpected response:', response);
            }
        } catch (error) {
            // makeMithrilRequest will handle error toasts and logging
            console.error('Error loading saving products:', error);
        }
    },

    loadSubunits: async function () {
        // OPTIMIZATION: Use cached subunit data from getUserData() instead of expensive API call
        // subunit_name is already stored in localStorage/sessionStorage during login

        try {
            const userData = await getUserData();

            if (userData.subunit_id && userData.subunit_name) {
                this.subunits = [{
                    id: userData.subunit_id,
                    nama: userData.subunit_name
                }];
                console.log('SimpananTab: Loaded subunit from user data:', userData.subunit_name);
            } else {
                // No subunit assigned - this shouldn't happen for managers using this module
                this.subunits = [];
                console.warn('SimpananTab: Manager has no subunit assigned - this module requires subunit assignment');
            }
        } catch (error) {
            console.error('SimpananTab: Error loading user data for subunits:', error);
            this.subunits = [];
        }

        this.subunitsLoading = false;
        m.redraw();
    },

    showForm: function () {
        // Close any existing edit form
        this.isEditFormVisible = false;
        this.editingProduct = null;

        this.isFormVisible = true;

        // Trigger Mithril redraw
        m.redraw();

        // Scroll to the add form after it's rendered
        setTimeout(() => {
            const addForm = document.querySelector('form');
            if (addForm) {
                addForm.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }, 100);
    },

    hideForm: function () {
        this.isFormVisible = false;
    },

    showEditForm: function (product) {
        // Close any existing add form
        this.isFormVisible = false;

        this.editingProduct = product;
        this.isEditFormVisible = true;

        // Scroll to the edit form after it's rendered
        setTimeout(() => {
            const editForm = document.querySelector('form');
            if (editForm) {
                editForm.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }, 100);
    },

    hideEditForm: function () {
        this.isEditFormVisible = false;
        this.editingProduct = null;
    },

    refreshData: function () {
        this.loadData();
    },


    deleteProduct: async function (productId) {
        console.log('SimpananTab: deleteProduct called with ID:', productId);
        showConfirmation(
            'Apakah Anda yakin ingin menghapus produk ini?',
            async () => {
                console.log('SimpananTab: User confirmed deletion');
                // Confirmed - proceed with deletion
                try {
                    console.log('SimpananTab: Starting product deletion for ID:', productId);
                    const response = await deleteMithril(`/api/saving-products/${productId}`, {
                        component: this,
                        showLoading: true,
                        showErrorToast: true // Let makeMithrilRequest handle error toasts
                    });

                    console.log('SimpananTab: Delete response:', response);
                    if (response.success) {
                        console.log('SimpananTab: Product deleted successfully');
                        toast.show('Produk berhasil dihapus', 'success');
                        this.loadData(); // Refresh the data
                    } else {
                        // This shouldn't happen as errors are handled by makeMithrilRequest
                        console.error('SimpananTab: Unexpected delete response:', response);
                    }
                } catch (error) {
                    // makeMithrilRequest will handle error toasts and logging
                    console.error('SimpananTab: Error deleting product:', error);
                }
            }
        );
    },

    view: function () {
        // Table header class constant
        const tableHeaderClass = 'px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider';

        return m('div', [
            // Header with title and add button
            m('div', { class: 'flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6' }, [
                m('div', [
                    m('h2', { class: 'text-xl font-bold text-gray-800 dark:text-white flex items-center' }, [
                        m('i', { class: 'ri-bank-line mr-2 text-orange-500' }),
                        'Produk Simpanan'
                    ]),
                    m('p', { class: 'text-sm text-gray-600 dark:text-gray-400 mt-1' }, 'Kelola produk simpanan unit simpan pinjam')
                ]),
                m('div', { class: 'flex space-x-2' }, [
                    m('button', {
                        class: 'bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-md flex items-center transition-colors duration-200 shadow-sm',
                        onclick: () => {
                            console.log('SimpananTab: Tambah Produk button clicked');
                            this.showForm();
                        }
                    }, [
                        m('i', { class: 'ri-add-line mr-2' }),
                        'Tambah Produk'
                    ])
                ])
            ]),

            // Stats cards
            this.savingProducts.length > 0 && m('div', { class: 'grid grid-cols-1 md:grid-cols-3 gap-4 mb-6' }, [
                m('div', { class: 'bg-white dark:bg-gray-800 rounded-lg shadow p-4 border-l-4 border-orange-500' }, [
                    m('div', { class: 'text-sm font-medium text-gray-500 dark:text-gray-400' }, 'Total Produk'),
                    m('div', { class: 'text-2xl font-bold text-gray-900 dark:text-white mt-1' }, this.savingProducts.length)
                ]),
                m('div', { class: 'bg-white dark:bg-gray-800 rounded-lg shadow p-4 border-l-4 border-blue-500' }, [
                    m('div', { class: 'text-sm font-medium text-gray-500 dark:text-gray-400' }, 'Dengan Bunga'),
                    m('div', { class: 'text-2xl font-bold text-gray-900 dark:text-white mt-1' },
                        this.savingProducts.filter(p => p.interest_rate && parseFloat(p.interest_rate) > 0).length)
                ]),
                m('div', { class: 'bg-white dark:bg-gray-800 rounded-lg shadow p-4 border-l-4 border-green-500' }, [
                    m('div', { class: 'text-sm font-medium text-gray-500 dark:text-gray-400' }, 'Aktif'),
                    m('div', { class: 'text-2xl font-bold text-gray-900 dark:text-white mt-1' },
                        this.savingProducts.filter(p => p.status !== 'inactive').length)
                ])
            ]),

            // Loading indicator with proper spinner
            this.isLoading ?
                m('div', { class: 'flex flex-col items-center justify-center py-12' }, [
                    m('div', { class: 'animate-spin rounded-full h-12 w-12 border-4 border-orange-200 border-t-orange-500 mb-4' }),
                    m('p', { class: 'text-gray-600 dark:text-gray-400 text-sm' }, 'Memuat data produk...')
                ]) :
                // Products table with improved styling
                m('div', { class: 'bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden border border-gray-200 dark:border-gray-700' }, [
                    m('div', { class: 'overflow-x-auto' }, [
                        m('table', { class: 'min-w-full divide-y divide-gray-200 dark:divide-gray-700' }, [
                            m('thead', { class: 'bg-gray-50 dark:bg-gray-700' }, [
                                m('tr', [
                                    m('th', { class: tableHeaderClass }, 'No'),
                                    m('th', { class: tableHeaderClass }, 'Kode'),
                                    m('th', { class: tableHeaderClass }, 'Nama Produk'),
                                    m('th', { class: tableHeaderClass }, getTerm('tableHeaders.subunit')),
                                    m('th', { class: tableHeaderClass.replace('text-left', 'text-right') }, 'Aksi')
                                ])
                            ]),
                            m('tbody', { class: 'bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700' }, [
                                this.savingProducts.length === 0 ?
                                    m('tr', [
                                        m('td', { class: 'px-6 py-12 text-center text-gray-500 dark:text-gray-400', colspan: '4' }, [
                                            m('div', { class: 'flex flex-col items-center justify-center' }, [
                                                m('i', { class: 'ri-bank-line text-3xl mb-2 text-gray-300 dark:text-gray-600' }),
                                                m('p', { class: 'font-medium' }, 'Tidak ada data produk simpanan'),
                                                m('p', { class: 'text-sm mt-1' }, 'Tekan tombol "Tambah Produk" untuk membuat produk baru')
                                            ])
                                        ])
                                    ]) :
                                    this.savingProducts.map((product, index) =>
                                        m('tr', {
                                            class: `hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-150 ${index % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-800/50'
                                                }`
                                        }, [
                                            m('td', { class: 'px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400' }, index + 1),
                                            m('td', { class: 'px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white' }, product.code),
                                            m('td', { class: 'px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300' }, product.name),
                                            m('td', { class: 'px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300' },
                                                product.subunit_id ?
                                                    (this.subunits.find(s => parseInt(s.id, 10) === parseInt(product.subunit_id, 10))?.nama || `ID: ${product.subunit_id}`) :
                                                    '-'
                                            ),
                                            m('td', { class: 'px-6 py-4 whitespace-nowrap text-right text-sm font-medium' }, [
                                                m('button', {
                                                    class: 'text-orange-600 hover:text-orange-900 dark:text-orange-400 dark:hover:text-orange-300 p-1 rounded-full hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-colors duration-200 mr-2',
                                                    onclick: () => this.showEditForm(product),
                                                    title: 'Edit'
                                                }, [
                                                    m('i', { class: 'ri-edit-line' })
                                                ]),
                                                m('button', {
                                                    class: 'text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 p-1 rounded-full hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors duration-200',
                                                    onclick: () => this.deleteProduct(product.id),
                                                    title: 'Hapus'
                                                }, [
                                                    m('i', { class: 'ri-delete-bin-line' })
                                                ])
                                            ])
                                        ])
                                    )
                            ])
                        ])
                    ])
                ]),

            // Add Product Form Modal
            this.isFormVisible && m(SimpananForm, {
                onHide: () => this.hideForm(),
                onRefresh: () => this.refreshData(),
                subunits: this.subunits
            }),

            // Edit Product Form Modal
            this.isEditFormVisible && m(EditSimpananForm, {
                product: this.editingProduct,
                onHide: () => this.hideEditForm(),
                onRefresh: () => this.refreshData(),
                subunits: this.subunits
            })
        ]);
    }
};

// Shared field: per-product opt-in for the subunit minimum-balance penalty (/umum preferensi)
const renderMinimumBalanceEnforcedField = (labelClass, form) =>
    m("div", { class: "pt-2 border-t border-gray-200 dark:border-gray-600" }, [
        m("div", { class: "flex items-start justify-between gap-4" }, [
            m("div", [
                m("label", {
                    class: labelClass,
                    for: "minimum_balance_enforced"
                }, "Saldo ditarik apabila di bawah saldo minimal selama kurun waktu tertentu"),
                m("p", { class: "mt-1 text-xs text-gray-500 dark:text-gray-400" }, "Mengacu ke pengaturan saldo minimal dan grace period di menu Umum. Berlaku pada penjadwalan (cron) debet otomatis.")
            ]),
            m("label", { class: "inline-flex items-center cursor-pointer mt-1" }, [
                m("input", {
                    type: "checkbox",
                    id: "minimum_balance_enforced",
                    class: "h-5 w-5 rounded border-gray-300 text-orange-600 focus:ring-orange-500 dark:bg-gray-700 dark:border-gray-600",
                    checked: form.formData.minimum_balance_enforced === 'Y',
                    onchange: e => form.handleInput('minimum_balance_enforced', e.target.checked ? 'Y' : 'N')
                }),
                m("span", { class: "ml-2 text-sm text-gray-700 dark:text-gray-300" }, form.formData.minimum_balance_enforced === 'Y' ? 'Ya' : 'Tidak')
            ])
        ])
    ]);

// Simpanan Form Component
const SimpananForm = {
    oninit: function (vnode) {
        debugComponentMount('SimpananForm', 'oninit started');
        this.isSubmitting = false;

        // Initialize form with subunit data from props
        const subunits = vnode.attrs.subunits || [];
        const currentSubunit = subunits.length > 0 ? subunits[0] : null;

        this.formData = {
            code: '',
            name: '',
            product_type: 'Biasa',
            subunit_id: currentSubunit ? currentSubunit.id : null,
            // Interest rate fields
            interest_rate: 0.00,
            interest_calculation_method: 'daily',
            interest_payment_frequency: 'monthly',
            // Balance limit fields
            minimum_balance: 0.00,
            // Per-product opt-in for the subunit minimum-balance penalty (/umum preferensi)
            minimum_balance_enforced: 'N',
            maximum_balance: '',
            minimum_initial_deposit: '',
            // Transaction limit fields
            minimum_deposit_amount: '',
            maximum_deposit_amount: '',
            minimum_withdrawal_amount: '',
            maximum_daily_withdrawal_amount: '',
            maximum_daily_withdrawal_count: '',
            // ATM specific limits
            atm_daily_limit_amount: '',
            atm_daily_limit_count: '',
            atm_transaction_limit: '',
            // Fee fields
            monthly_maintenance_fee: 0.00,
            withdrawal_fee: 0.00,
            deposit_fee: 0.00,
            inactive_fee: 0.00,
            // Status and activation fields
            is_active: 'Y',
            activation_date: '',
            expiry_date: '',
            // Customer uniqueness constraint
            unique_per_customer: 'N',
            // Loan disbursement requirement
            required_for_loan_disbursement: 'N',
            // PHU allocation basis flag
            phu_basis: 'N',
            // Description fields
            description: '',
            eligibility_criteria: '',
            benefits: ''
        };
        this.errors = {};

        // Capture vnode attributes
        this.onHide = vnode.attrs.onHide;
        this.onRefresh = vnode.attrs.onRefresh;
        this.subunits = vnode.attrs.subunits || [];

        debugComponentMount('SimpananForm', 'oninit completed');
    },

    oncreate: function (vnode) {
        debugComponentMount('SimpananForm', 'oncreate - DOM mounted');
        // Ensure form buttons are properly attached
        const formElement = vnode.dom.querySelector('form');
        const submitButton = vnode.dom.querySelector('button[type="submit"]');

        console.log('SimpananForm: Form element found:', !!formElement);
        console.log('SimpananForm: Submit button found:', !!submitButton);

        if (submitButton) {
            console.log('SimpananForm: Submit button state:', {
                disabled: submitButton.disabled,
                textContent: submitButton.textContent
            });
        }
    },

    getCurrentUserSubunitId: async function () {
        try {
            const userData = await getUserData();
            return userData.subunit_id || null;
        } catch (error) {
            console.error('SimpananTab: Error getting current user subunit ID:', error);
            return null;
        }
    },

    getCurrentSubunitName: async function () {
        try {
            const userData = await getUserData();
            const subunitId = userData.subunit_id;

            if (!subunitId) {
                return 'Subunit tidak ditentukan - Harap login dengan manajer yang benar';
            }

            if (!userData.subunit_assigned) {
                return 'Semua Unit Bisnis';
            }

            return userData.subunit_name || `Subunit ID: ${subunitId}`;
        } catch (error) {
            console.error('SimpananTab: Error getting current subunit name:', error);
            return 'Error loading subunit information';
        }
    },

    handleInput: function (field, value) {
        this.formData[field] = value;
        // Clear error when user starts typing
        if (this.errors[field]) {
            delete this.errors[field];
        }
    },

    // Handle decimal input for interest rates (like PinjamanTab approach)
    handleDecimalInput: function (field, value) {
        this.formData[field] = value;

        // Clear error when user starts typing
        if (this.errors[field]) {
            delete this.errors[field];
        }
    },

    // Format decimal number to 2 decimal places for display
    formatDecimalForDisplay: function (value) {
        if (value === '' || value === null || value === undefined) {
            return '';
        }

        const num = parseFloat(value);
        if (isNaN(num)) {
            return '';
        }

        // Ensure it's within valid range for display
        if (num < 0 || num > 99.99) {
            return num.toFixed(2);
        }

        return num.toFixed(2);
    },

    // Handle formatted input for number fields
    handleFormattedInput: function (field, value) {
        // Store the formatted value for display
        this.formData[field + '_formatted'] = value;

        // Parse and store the numeric value for submission
        const numericValue = this.parseFormattedNumber(value);
        this.formData[field] = numericValue;

        // Clear error when user starts typing
        if (this.errors[field]) {
            delete this.errors[field];
        }
    },

    // Format number with thousand separators for display
    formatNumberForDisplay: function (value) {
        if (value === '' || value === null || value === undefined) {
            return '';
        }

        // Convert to string and remove non-numeric characters except decimal point and comma
        let cleanValue = value.toString().replace(/[^\d.,-]/g, '');

        // Handle negative numbers
        const isNegative = cleanValue.startsWith('-');
        if (isNegative) {
            cleanValue = cleanValue.substring(1);
        }

        // Handle decimal values (both . and , as decimal separators)
        const dotIndex = cleanValue.indexOf('.');
        const commaIndex = cleanValue.indexOf(',');

        // Determine which is the decimal separator (last one)
        let decimalSeparator = null;
        let decimalIndex = -1;

        if (dotIndex !== -1 && commaIndex !== -1) {
            // Both present, last one is decimal separator
            if (dotIndex > commaIndex) {
                decimalSeparator = '.';
                decimalIndex = dotIndex;
            } else {
                decimalSeparator = ',';
                decimalIndex = commaIndex;
            }
        } else if (dotIndex !== -1) {
            decimalSeparator = '.';
            decimalIndex = dotIndex;
        } else if (commaIndex !== -1) {
            decimalSeparator = ',';
            decimalIndex = commaIndex;
        }

        if (decimalIndex !== -1) {
            const integerPart = cleanValue.substring(0, decimalIndex);
            const decimalPart = cleanValue.substring(decimalIndex + 1).substring(0, 2); // Limit to 2 decimal places

            if (integerPart === '') {
                return (isNegative ? '-' : '') + '0' + decimalSeparator + decimalPart;
            }

            // Format integer part with thousand separators
            const formattedInteger = parseInt(integerPart, 10).toLocaleString('id-ID');
            return (isNegative ? '-' : '') + formattedInteger + decimalSeparator + decimalPart;
        } else {
            // Format integer values
            const num = parseInt(cleanValue, 10);
            if (isNaN(num)) {
                return '';
            }
            return (isNegative ? '-' : '') + num.toLocaleString('id-ID');
        }
    },

    // Parse formatted number back to float
    parseFormattedNumber: function (value) {
        if (value === '' || value === null || value === undefined) {
            return '';
        }

        // Normalize decimal separator (convert comma to dot for decimal)
        let normalizedValue = value.toString().replace(/[^\d.,-]/g, ''); // Remove non-numeric except ., and -

        // Handle negative numbers
        const isNegative = normalizedValue.startsWith('-');
        if (isNegative) {
            normalizedValue = normalizedValue.substring(1);
        }

        // Check if there's a clear decimal separator (dot or comma followed by 0-2 digits)
        const dotIndex = normalizedValue.lastIndexOf('.');
        const commaIndex = normalizedValue.lastIndexOf(',');

        let hasDecimal = false;
        let decimalSeparator = null;
        let decimalIndex = -1;

        // Check for dot as decimal separator (must have digits before and optionally after)
        if (dotIndex !== -1) {
            const beforeDot = normalizedValue.substring(0, dotIndex);
            const afterDot = normalizedValue.substring(dotIndex + 1);

            // If there are digits before the dot, and it's not clearly a thousand separator pattern
            if (beforeDot.length > 0 && /^\d+$/.test(beforeDot)) {
                // Check if this looks like a thousand separator (dot followed by exactly 3 digits, preceded by 3+ digits)
                const isThousandSeparator = afterDot.length === 3 && /^\d{3}$/.test(afterDot) &&
                    beforeDot.length >= 3 && /^\d{1,3}$/.test(beforeDot.slice(-3));

                if (!isThousandSeparator) {
                    hasDecimal = true;
                    decimalSeparator = '.';
                    decimalIndex = dotIndex;
                }
            }
        }

        // Check for comma as decimal separator if no dot decimal found
        if (!hasDecimal && commaIndex !== -1) {
            const beforeComma = normalizedValue.substring(0, commaIndex);
            const afterComma = normalizedValue.substring(commaIndex + 1);

            // If there are digits before the comma, and it's not clearly a thousand separator pattern
            if (beforeComma.length > 0 && /^\d+$/.test(beforeComma)) {
                // Similar logic for comma as thousand separator
                const isThousandSeparator = afterComma.length === 3 && /^\d{3}$/.test(afterComma) &&
                    beforeComma.length >= 3 && /^\d{1,3}$/.test(beforeComma.slice(-3));

                if (!isThousandSeparator) {
                    hasDecimal = true;
                    decimalSeparator = ',';
                    decimalIndex = commaIndex;
                }
            }
        }

        if (hasDecimal) {
            // Split at decimal separator
            let integerPart = normalizedValue.substring(0, decimalIndex);
            let decimalPart = normalizedValue.substring(decimalIndex + 1);

            // Remove thousand separators from integer part
            integerPart = integerPart.replace(/[.,]/g, '');

            // Reconstruct with dot as decimal separator
            normalizedValue = integerPart + '.' + decimalPart;
        } else {
            // No decimal separator found, treat all dots and commas as thousand separators
            normalizedValue = normalizedValue.replace(/[.,]/g, '');
        }

        const num = parseFloat((isNegative ? '-' : '') + normalizedValue);
        return isNaN(num) ? '' : num;
    },

    validateForm: function () {
        this.errors = {};
        let isValid = true;

        if (!this.formData.code.trim()) {
            this.errors.code = "Kode produk harus diisi";
            isValid = false;
        }

        if (!this.formData.name.trim()) {
            this.errors.name = "Nama produk harus diisi";
            isValid = false;
        }

        // Validate interest rate (should be between 0 and 99.99)
        const interestRate = parseFloat(this.formData.interest_rate) || 0;
        if (interestRate < 0 || interestRate > 99.99) {
            this.errors.interest_rate = "Suku Bunga harus antara 0% dan 99,99%";
            isValid = false;
        }

        // Validate balance limits
        const minBalance = parseFloat(this.formData.minimum_balance) || 0;
        const maxBalanceInput = this.formData.maximum_balance;
        const maxBalance = (maxBalanceInput && parseFloat(maxBalanceInput) > 0) ? parseFloat(maxBalanceInput) : null;
        const minInitialDeposit = this.formData.minimum_initial_deposit !== '' && this.formData.minimum_initial_deposit !== null && this.formData.minimum_initial_deposit !== undefined ? parseFloat(this.formData.minimum_initial_deposit) : null;

        if (maxBalance !== null && maxBalance <= minBalance) {
            this.errors.maximum_balance = "Saldo Maximum harus lebih besar dari Saldo Minimum";
            isValid = false;
        }

        if (minInitialDeposit !== null && minInitialDeposit < minBalance) {
            this.errors.minimum_initial_deposit = "Setoran Awal Minimum tidak boleh kurang dari Saldo Minimum";
            isValid = false;
        }

        return isValid;
    },

    handleSubmit: function (e) {
        console.log('SimpananForm: handleSubmit called', { event: e, isSubmitting: this.isSubmitting });
        e.preventDefault();

        if (!this.validateForm()) {
            console.log('SimpananForm: Form validation failed');
            toast.show('Mohon perbaiki kesalahan pada form terlebih dahulu', 'warning');
            return;
        }

        console.log('SimpananForm: Form validation passed, starting submission');
        this.isSubmitting = true;
        // Force immediate UI update to show loading state
        m.redraw.sync();

        // Ensure subunit_id is properly set
        let subunitId = this.formData.subunit_id;
        if (!subunitId) {
            console.log('SimpananForm: No subunit_id in form data, this should not happen');
        }


        // Prepare all fields from schema, ensuring proper types
        const submitData = {
            code: this.formData.code,
            name: this.formData.name,
            subunit_id: subunitId ? parseInt(subunitId, 10) : null,
            // Interest rate fields
            interest_rate: parseFloat(this.formData.interest_rate) || 0.00,
            interest_calculation_method: this.formData.interest_calculation_method || null,
            interest_payment_frequency: this.formData.interest_payment_frequency || null,
            // Balance limit fields
            minimum_balance: parseFloat(this.formData.minimum_balance) || 0.00,
            // Per-product opt-in for the subunit minimum-balance penalty (/umum preferensi)
            minimum_balance_enforced: this.formData.minimum_balance_enforced || 'N',
            maximum_balance: (this.formData.maximum_balance && parseFloat(this.formData.maximum_balance) > 0) ? parseFloat(this.formData.maximum_balance) : null,
            minimum_initial_deposit: this.formData.minimum_initial_deposit ? parseFloat(this.formData.minimum_initial_deposit) : null,
            // Transaction limit fields
            minimum_deposit_amount: this.formData.minimum_deposit_amount ? parseFloat(this.formData.minimum_deposit_amount) : null,
            maximum_deposit_amount: this.formData.maximum_deposit_amount ? parseFloat(this.formData.maximum_deposit_amount) : null,
            minimum_withdrawal_amount: this.formData.minimum_withdrawal_amount ? parseFloat(this.formData.minimum_withdrawal_amount) : null,
            maximum_daily_withdrawal_amount: this.formData.maximum_daily_withdrawal_amount ? parseFloat(this.formData.maximum_daily_withdrawal_amount) : null,
            maximum_daily_withdrawal_count: this.formData.maximum_daily_withdrawal_count ? parseInt(this.formData.maximum_daily_withdrawal_count) : null,
            // ATM specific limits
            atm_daily_limit_amount: this.formData.atm_daily_limit_amount ? parseFloat(this.formData.atm_daily_limit_amount) : null,
            atm_daily_limit_count: this.formData.atm_daily_limit_count ? parseInt(this.formData.atm_daily_limit_count) : null,
            atm_transaction_limit: this.formData.atm_transaction_limit ? parseFloat(this.formData.atm_transaction_limit) : null,
            // Fee fields
            monthly_maintenance_fee: parseFloat(this.formData.monthly_maintenance_fee) || 0.00,
            withdrawal_fee: parseFloat(this.formData.withdrawal_fee) || 0.00,
            deposit_fee: parseFloat(this.formData.deposit_fee) || 0.00,
            inactive_fee: parseFloat(this.formData.inactive_fee) || 0.00,
            // Status and activation fields
            is_active: this.formData.is_active || 'Y',
            activation_date: this.formData.activation_date ? new Date(this.formData.activation_date).toISOString() : null,
            expiry_date: this.formData.expiry_date ? new Date(this.formData.expiry_date).toISOString() : null,
            // Customer uniqueness constraint
            unique_per_customer: this.formData.unique_per_customer || 'N',
            // Loan disbursement requirement
            required_for_loan_disbursement: this.formData.required_for_loan_disbursement || 'N',
            // PHU allocation basis flag
            phu_basis: this.formData.phu_basis || 'N',
            // Description fields
            description: this.formData.description || null,
            eligibility_criteria: this.formData.eligibility_criteria || null,
            benefits: this.formData.benefits || null
        };
        console.log('Final data for submission:', submitData);

        console.log('SimpananForm: Submitting data to API:', submitData);

        // Submit form data using enhanced Mithril utility with improved error handling
        postMithril("/api/saving-products", submitData, {
            component: this,
            showLoading: true,
            showErrorToast: false // Handle errors manually for field-specific cases
        }).then(response => {
            console.log('SimpananForm: API response received:', response);
            this.isSubmitting = false;
            m.redraw();

            if (response.success) {
                console.log('SimpananForm: Submission successful');
                // Close form and refresh data
                this.onHide();
                this.onRefresh();
                toast.show("Produk simpanan berhasil disimpan", "success");
            } else {
                console.error('SimpananForm: Submission failed:', response.error);
                // Handle specific error types that need field-focus
                if (response.error && typeof response.error === 'string' && response.error.includes('sudah digunakan')) {
                    // Duplicate code error - set field-specific error and show toast
                    this.errors.code = response.error;
                    toast.show(response.error, "error");
                    console.log('SimpananForm: Showing duplicate code error toast');
                    // Focus on the code field to help user fix the issue
                    setTimeout(() => {
                        const codeField = document.querySelector('input[id="code"]');
                        if (codeField) {
                            codeField.focus();
                            codeField.select();
                        }
                    }, 100);
                } else {
                    // For other errors, let makeMithrilRequest handle them
                    throw new Error(response.error || "Gagal menyimpan produk simpanan");
                }
            }
        }).catch(error => {
            console.error('SimpananForm: Error in submission:', error);
            this.isSubmitting = false;
            m.redraw.sync();

            // Check for duplicate code error in various formats
            const isDuplicateCodeError =
                (error.message && error.message.includes('sudah digunakan')) ||
                (error.error && typeof error.error === 'string' && error.error.includes('sudah digunakan')) ||
                (error.status === 409);

            if (isDuplicateCodeError) {
                const duplicateErrorMessage = error.message || error.error || "Kode produk sudah digunakan. Silakan gunakan kode yang berbeda.";
                this.errors.code = duplicateErrorMessage;

                // Show toast notification
                showErrorToast(duplicateErrorMessage);

                // Focus on code field for user to fix
                setTimeout(() => {
                    const codeField = document.querySelector('input[id="code"]');
                    if (codeField) {
                        codeField.focus();
                        codeField.select();
                    }
                }, 100);
            } else {
                // For all other errors, let makeMithrilRequest handle them
                // The utility will show appropriate error toasts and handle redirects
                this.errors.general = error.message || "Terjadi kesalahan saat menyimpan produk";
                showErrorToast(this.errors.general);
            }
        });
    },

    view: function (vnode) {
        const { onHide } = vnode.attrs;
        const subunits = vnode.attrs.subunits || [
            { id: 1, nama: 'Test Subunit 1' },
            { id: 2, nama: 'Test Subunit 2' }
        ];

        // Form input class constant
        const inputClassBase = "mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-orange-500 focus:ring-orange-500 dark:bg-gray-700 dark:text-white";
        const labelClass = "block text-sm font-medium text-gray-700 dark:text-gray-300";

        return m("div", { class: "fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" }, [
            m("div", {
                class: "bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-screen overflow-y-auto",
                onclick: (e) => e.stopPropagation() // Prevent click inside modal from closing it
            }, [
                // Header
                m("div", { class: "bg-white dark:bg-gray-800 px-6 py-4 border-b border-gray-200 dark:border-gray-700 rounded-t-lg" }, [
                    m("div", { class: "flex justify-between items-center" }, [
                        m("h3", { class: "text-lg font-medium text-gray-900 dark:text-white" }, "Tambah Produk Simpanan"),
                        m("button", {
                            type: "button",
                            class: "text-gray-400 hover:text-gray-500 focus:outline-none",
                            onclick: () => this.onHide()
                        }, [
                            m("svg", {
                                class: "h-6 w-6",
                                fill: "none",
                                viewBox: "0 0 24 24",
                                stroke: "currentColor"
                            }, [
                                m("path", {
                                    d: "M6 18L18 6M6 6l12 12",
                                    "stroke-linecap": "round",
                                    "stroke-linejoin": "round",
                                    "stroke-width": "2"
                                })
                            ])
                        ])
                    ]),
                    m("p", { class: "mt-1 text-sm text-gray-600 dark:text-gray-400" }, "Masukkan informasi produk simpanan baru")
                ]),

                // Form
                m("form", { onsubmit: (e) => this.handleSubmit(e) }, [
                    m("div", { class: "px-6 py-4 space-y-4" }, [
                        // Subunit field moved here from outside the form
                        m("div", { class: "px-6 py-4 space-y-6" }, [
                            // Code field
                            m("div", [
                                m("label", {
                                    class: labelClass,
                                    for: "code"
                                }, "Kode Produk"),
                                m("input", {
                                    type: "text",
                                    id: "code",
                                    class: inputClassBase,
                                    placeholder: "Masukkan kode produk (mis: TAB01)",
                                    value: this.formData.code,
                                    oninput: e => this.handleInput('code', e.target.value)
                                }),
                                this.errors.code && m("p", { class: "mt-1 text-sm text-red-600 dark:text-red-400" }, this.errors.code)
                            ]),



                            // Product Type field
                            m("div", [
                                m("label", { class: labelClass }, "Jenis Produk"),
                                m("div", { class: "flex items-center space-x-4 mt-2" }, [
                                    m("label", { class: "inline-flex items-center cursor-pointer" }, [
                                        m("input", {
                                            type: "radio",
                                            name: "product_type",
                                            value: "Biasa",
                                            checked: this.formData.product_type === 'Biasa',
                                            onchange: e => this.handleInput('product_type', e.target.value),
                                            class: "form-radio h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300"
                                        }),
                                        m("span", { class: "ml-2 text-sm text-gray-700 dark:text-gray-300" }, "Biasa")
                                    ]),
                                    m("label", { class: "inline-flex items-center cursor-pointer" }, [
                                        m("input", {
                                            type: "radio",
                                            name: "product_type",
                                            value: "Berjangka",
                                            checked: this.formData.product_type === 'Berjangka',
                                            onchange: e => this.handleInput('product_type', e.target.value),
                                            class: "form-radio h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300"
                                        }),
                                        m("span", { class: "ml-2 text-sm text-gray-700 dark:text-gray-300" }, "Berjangka")
                                    ])
                                ])
                            ]),

                            // Name field
                            m("div", [
                                m("label", {
                                    class: labelClass,
                                    for: "name"
                                }, "Nama Produk"),
                                m("input", {
                                    type: "text",
                                    id: "name",
                                    class: inputClassBase,
                                    placeholder: "Masukkan nama produk",
                                    value: this.formData.name,
                                    oninput: e => this.handleInput('name', e.target.value)
                                }),
                                this.errors.name && m("p", { class: "mt-1 text-sm text-red-600 dark:text-red-400" }, this.errors.name)
                            ]),

                            // Subunit field - showing current user's subunit from props
                            m("div", [
                                m("label", {
                                    class: labelClass,
                                    for: "subunit"
                                }, getTerm('tableHeaders.subunit')),
                                m("input", {
                                    type: "text",
                                    id: "subunit",
                                    class: inputClassBase,
                                    value: subunits.length > 0 ? subunits[0].nama : 'Subunit tidak ditentukan - Harap login dengan manajer yang benar',
                                    disabled: true
                                }),
                                m("p", { class: "mt-1 text-sm text-gray-500 dark:text-gray-400" }, "Unit/Koperasi ditentukan berdasarkan pengguna saat ini")
                            ]),

                            // Interest rate section
                            m("div", { class: "space-y-4 bg-gray-50 dark:bg-gray-700 p-4 rounded-lg" }, [
                                m("h4", { class: "text-md font-semibold text-gray-800 dark:text-white" }, "Pengaturan Bunga"),
                                m("div", [
                                    m("label", {
                                        class: labelClass,
                                        for: "interest_rate"
                                    }, "Suku Bunga (% per tahun)"),
                                    m("input", {
                                        type: "number",
                                        id: "interest_rate",
                                        step: "0.01",
                                        min: "0",
                                        max: "99.99",
                                        class: inputClassBase,
                                        placeholder: "0.00",
                                        value: this.formData.interest_rate,
                                        oninput: e => this.handleDecimalInput('interest_rate', e.target.value)
                                    }),
                                    this.errors.interest_rate && m("p", { class: "mt-1 text-sm text-red-600 dark:text-red-400" }, this.errors.interest_rate)
                                ]),
                                m("div", [
                                    m("label", {
                                        class: labelClass,
                                        for: "interest_calculation_method"
                                    }, "Metode Perhitungan Bunga"),
                                    m("select", {
                                        id: "interest_calculation_method",
                                        class: inputClassBase,
                                        value: this.formData.interest_calculation_method || "daily_balance",
                                        onchange: e => {
                                            this.formData.interest_calculation_method = e.target.value;
                                            this.formData.interest_payment_frequency = 'monthly'; // Default frequency to monthly
                                        }
                                    }, [
                                        m("option", { value: "daily_balance" }, "Metode Saldo Harian"),
                                        m("option", { value: "lowest_balance" }, "Metode Saldo Terendah"),
                                        m("option", { value: "average_balance" }, "Metode Saldo Rata-rata")
                                    ])
                                ])
                            ]),

                            // Balance limits section
                            m("div", { class: "space-y-4 bg-gray-50 dark:bg-gray-700 p-4 rounded-lg" }, [
                                m("h4", { class: "text-md font-semibold text-gray-800 dark:text-white" }, "Batas Saldo"),
                                m("div", [
                                    m("label", {
                                        class: labelClass,
                                        for: "minimum_balance"
                                    }, "Saldo Minimum (Rp)"),
                                    m("input", {
                                        type: "text",
                                        id: "minimum_balance",
                                        class: inputClassBase,
                                        placeholder: "0",
                                        value: this.formData.minimum_balance_formatted || this.formData.minimum_balance || '',
                                        oninput: e => this.handleFormattedInput('minimum_balance', e.target.value),
                                        onblur: e => {
                                            // Format the value when user leaves the field
                                            const formatted = this.formatNumberForDisplay(this.formData.minimum_balance);
                                            this.formData.minimum_balance_formatted = formatted;
                                            e.target.value = formatted;
                                        }
                                    })
                                ]),
                                m("div", [
                                    m("label", {
                                        class: labelClass,
                                        for: "maximum_balance"
                                    }, "Saldo Maximum (Rp)"),
                                    m("input", {
                                        type: "text",
                                        id: "maximum_balance",
                                        class: inputClassBase,
                                        placeholder: "Kosongkan untuk unlimited",
                                        value: this.formData.maximum_balance_formatted || this.formData.maximum_balance || '',
                                        oninput: e => this.handleFormattedInput('maximum_balance', e.target.value),
                                        onblur: e => {
                                            // Format the value when user leaves the field
                                            const formatted = this.formatNumberForDisplay(this.formData.maximum_balance);
                                            this.formData.maximum_balance_formatted = formatted;
                                            e.target.value = formatted;
                                        }
                                    }),
                                    m("p", { class: "mt-1 text-sm text-gray-500 dark:text-gray-400" }, "Kosongkan atau isi 0 untuk unlimited")
                                ]),
                                m("div", [
                                    m("label", {
                                        class: labelClass,
                                        for: "minimum_initial_deposit"
                                    }, "Setoran Awal Minimum (Rp)"),
                                    m("input", {
                                        type: "text",
                                        id: "minimum_initial_deposit",
                                        class: inputClassBase,
                                        placeholder: "0",
                                        value: this.formData.minimum_initial_deposit_formatted || this.formData.minimum_initial_deposit || '',
                                        oninput: e => this.handleFormattedInput('minimum_initial_deposit', e.target.value),
                                        onblur: e => {
                                            // Format the value when user leaves the field
                                            const formatted = this.formatNumberForDisplay(this.formData.minimum_initial_deposit);
                                            this.formData.minimum_initial_deposit_formatted = formatted;
                                            e.target.value = formatted;
                                        }
                                    })
                                ]),
                                renderMinimumBalanceEnforcedField(labelClass, this)
                            ]),

                            // Fee Configuration section
                            m("div", { class: "space-y-4 bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-700" }, [
                                m("h4", { class: "text-md font-semibold text-gray-800 dark:text-white" }, "Konfigurasi Biaya"),
                                m("div", [
                                    m("label", {
                                        class: labelClass,
                                        for: "withdrawal_fee"
                                    }, "Biaya Penarikan (Rp)"),
                                    m("input", {
                                        type: "text",
                                        id: "withdrawal_fee",
                                        class: inputClassBase,
                                        placeholder: "0",
                                        value: this.formData.withdrawal_fee_formatted || this.formData.withdrawal_fee || '',
                                        oninput: e => this.handleFormattedInput('withdrawal_fee', e.target.value),
                                        onblur: e => {
                                            // Format the value when user leaves the field
                                            const formatted = this.formatNumberForDisplay(this.formData.withdrawal_fee);
                                            this.formData.withdrawal_fee_formatted = formatted;
                                            e.target.value = formatted;
                                        }
                                    }),
                                    m("p", { class: "mt-1 text-sm text-gray-500 dark:text-gray-400" }, "Biaya yang dikenakan saat melakukan penarikan")
                                ]),
                                m("div", [
                                    m("label", {
                                        class: labelClass,
                                        for: "deposit_fee"
                                    }, "Biaya Setoran (Rp)"),
                                    m("input", {
                                        type: "text",
                                        id: "deposit_fee",
                                        class: inputClassBase,
                                        placeholder: "0",
                                        value: this.formData.deposit_fee_formatted || this.formData.deposit_fee || '',
                                        oninput: e => this.handleFormattedInput('deposit_fee', e.target.value),
                                        onblur: e => {
                                            // Format the value when user leaves the field
                                            const formatted = this.formatNumberForDisplay(this.formData.deposit_fee);
                                            this.formData.deposit_fee_formatted = formatted;
                                            e.target.value = formatted;
                                        }
                                    }),
                                    m("p", { class: "mt-1 text-sm text-gray-500 dark:text-gray-400" }, "Biaya yang dikenakan saat melakukan setoran")
                                ]),
                                m("div", [
                                    m("label", {
                                        class: labelClass,
                                        for: "monthly_maintenance_fee"
                                    }, "Biaya Administrasi Bulanan (Rp)"),
                                    m("input", {
                                        type: "text",
                                        id: "monthly_maintenance_fee",
                                        class: inputClassBase,
                                        placeholder: "0",
                                        value: this.formData.monthly_maintenance_fee_formatted || this.formData.monthly_maintenance_fee || '',
                                        oninput: e => this.handleFormattedInput('monthly_maintenance_fee', e.target.value),
                                        onblur: e => {
                                            // Format the value when user leaves the field
                                            const formatted = this.formatNumberForDisplay(this.formData.monthly_maintenance_fee);
                                            this.formData.monthly_maintenance_fee_formatted = formatted;
                                            e.target.value = formatted;
                                        }
                                    }),
                                    m("p", { class: "mt-1 text-sm text-gray-500 dark:text-gray-400" }, "Biaya administrasi yang dikenakan setiap bulan")
                                ]),
                                m("div", [
                                    m("label", {
                                        class: labelClass,
                                        for: "inactive_fee"
                                    }, "Biaya Tidak Aktif (Rp)"),
                                    m("input", {
                                        type: "text",
                                        id: "inactive_fee",
                                        class: inputClassBase,
                                        placeholder: "0",
                                        value: this.formData.inactive_fee_formatted || this.formData.inactive_fee || '',
                                        oninput: e => this.handleFormattedInput('inactive_fee', e.target.value),
                                        onblur: e => {
                                            // Format the value when user leaves the field
                                            const formatted = this.formatNumberForDisplay(this.formData.inactive_fee);
                                            this.formData.inactive_fee_formatted = formatted;
                                            e.target.value = formatted;
                                        }
                                    }),
                                    m("p", { class: "mt-1 text-sm text-gray-500 dark:text-gray-400" }, "Biaya yang dikenakan untuk rekening yang tidak aktif")
                                ])
                            ]),

                            // Status and Description section
                            m("div", { class: "space-y-4 bg-gray-50 dark:bg-gray-700 p-4 rounded-lg" }, [
                                m("h4", { class: "text-md font-semibold text-gray-800 dark:text-white" }, "Status & Deskripsi"),
                                m("div", [
                                    m("label", {
                                        class: labelClass,
                                        for: "is_active"
                                    }, "Status Produk"),
                                    m("select", {
                                        id: "is_active",
                                        class: inputClassBase,
                                        value: this.formData.is_active,
                                        oninput: e => this.handleInput('is_active', e.target.value)
                                    }, [
                                        m("option", { value: "Y" }, "Aktif"),
                                        m("option", { value: "N" }, "Tidak Aktif")
                                    ])
                                ]),
                                m("div", [
                                    m("label", {
                                        class: labelClass,
                                        for: "unique_per_customer"
                                    }, "Unik per Nasabah"),
                                    m("select", {
                                        id: "unique_per_customer",
                                        class: inputClassBase,
                                        value: this.formData.unique_per_customer,
                                        oninput: e => this.handleInput('unique_per_customer', e.target.value)
                                    }, [
                                        m("option", { value: "N" }, "Tidak"),
                                        m("option", { value: "Y" }, "Ya")
                                    ]),
                                    m("p", { class: "mt-1 text-sm text-gray-500 dark:text-gray-400" }, "Jika Ya, setiap nasabah hanya boleh memiliki satu rekening untuk produk ini")
                                ]),
                                m("div", [
                                    m("label", {
                                        class: labelClass,
                                        for: "required_for_loan_disbursement"
                                    }, "Wajib untuk Pencairan Pinjaman"),
                                    m("select", {
                                        id: "required_for_loan_disbursement",
                                        class: inputClassBase,
                                        value: this.formData.required_for_loan_disbursement,
                                        oninput: e => this.handleInput('required_for_loan_disbursement', e.target.value)
                                    }, [
                                        m("option", { value: "N" }, "Tidak"),
                                        m("option", { value: "Y" }, "Ya")
                                    ]),
                                    m("p", { class: "mt-1 text-sm text-gray-500 dark:text-gray-400" }, "Jika Ya, nasabah harus memiliki rekening aktif untuk produk ini sebelum dapat mencairkan pinjaman")
                                ]),
                                m("div", [
                                    m("label", {
                                        class: labelClass,
                                        for: "phu_basis"
                                    }, "Menjadi dasar perhitungan PHU"),
                                    m("select", {
                                        id: "phu_basis",
                                        class: inputClassBase,
                                        value: this.formData.phu_basis,
                                        oninput: e => this.handleInput('phu_basis', e.target.value)
                                    }, [
                                        m("option", { value: "N" }, "Tidak"),
                                        m("option", { value: "Y" }, "Ya")
                                    ]),
                                    m("p", { class: "mt-1 text-sm text-gray-500 dark:text-gray-400" }, "Jika Ya, saldo dan mutasi produk simpanan ini akan dihitung sebagai basis alokasi PHU anggota")
                                ]),
                                m("div", [
                                    m("label", {
                                        class: labelClass,
                                        for: "description"
                                    }, "Deskripsi"),
                                    m("textarea", {
                                        id: "description",
                                        rows: "3",
                                        class: inputClassBase,
                                        placeholder: "Deskripsi produk simpanan",
                                        value: this.formData.description,
                                        oninput: e => this.handleInput('description', e.target.value)
                                    })
                                ])
                            ])
                        ]),

                        m("div", { class: "bg-gray-50 dark:bg-gray-700 px-6 py-3 rounded-b-lg flex justify-end space-x-3" }, [
                            m("button", {
                                type: "button",
                                class: "inline-flex justify-center py-2 px-4 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-600 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500",
                                onclick: () => this.onHide()
                            }, "Batal"),
                            m("button", {
                                type: "submit",
                                class: "inline-flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed",
                                disabled: this.isSubmitting,
                                onclick: (e) => {
                                    console.log('SimpananForm: Submit button clicked', { event: e, isSubmitting: this.isSubmitting });
                                }
                            }, [
                                this.isSubmitting && m("svg", {
                                    class: "animate-spin -ml-1 mr-2 h-4 w-4 text-white",
                                    fill: "none",
                                    viewBox: "0 0 24 24"
                                }, [
                                    m("circle", {
                                        class: "opacity-25",
                                        cx: "12",
                                        cy: "12",
                                        r: "10",
                                        stroke: "currentColor",
                                        "stroke-width": "4"
                                    }),
                                    m("path", {
                                        class: "opacity-75",
                                        fill: "currentColor",
                                        d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                    })
                                ]),
                                this.isSubmitting ? "Menyimpan..." : "Simpan"
                            ])
                        ])
                    ])
                ])])])
    }
};


// Edit Simpanan Form Component
const EditSimpananForm = {
    oninit: function (vnode) {
        debugComponentMount('EditSimpananForm', 'oninit started');
        const product = vnode.attrs.product;
        this.isSubmitting = false;
        this.formData = {
            code: product.code || '',
            name: product.name || '',
            product_type: product.product_type || 'Biasa',
            subunit_id: product.subunit_id,
            // Interest rate fields
            interest_rate: product.interest_rate || 0.00,
            interest_calculation_method: product.interest_calculation_method || 'daily',
            interest_payment_frequency: product.interest_payment_frequency || 'monthly',
            // Balance limit fields
            minimum_balance: product.minimum_balance || 0.00,
            // Per-product opt-in for the subunit minimum-balance penalty (/umum preferensi)
            minimum_balance_enforced: product.minimum_balance_enforced || 'N',
            maximum_balance: (product.maximum_balance !== null && product.maximum_balance !== undefined) ? product.maximum_balance : '',
            minimum_initial_deposit: (product.minimum_initial_deposit !== null && product.minimum_initial_deposit !== undefined) ? product.minimum_initial_deposit : '',
            // Transaction limit fields
            minimum_deposit_amount: product.minimum_deposit_amount || '',
            maximum_deposit_amount: product.maximum_deposit_amount || '',
            minimum_withdrawal_amount: product.minimum_withdrawal_amount || '',
            maximum_daily_withdrawal_amount: product.maximum_daily_withdrawal_amount || '',
            maximum_daily_withdrawal_count: product.maximum_daily_withdrawal_count || '',
            // ATM specific limits
            atm_daily_limit_amount: product.atm_daily_limit_amount || '',
            atm_daily_limit_count: product.atm_daily_limit_count || '',
            atm_transaction_limit: product.atm_transaction_limit || '',
            // Fee fields
            monthly_maintenance_fee: product.monthly_maintenance_fee || 0.00,
            withdrawal_fee: product.withdrawal_fee || 0.00,
            deposit_fee: product.deposit_fee || 0.00,
            inactive_fee: product.inactive_fee || 0.00,
            // Status and activation fields
            is_active: product.is_active || 'Y',
            activation_date: product.activation_date || '',
            expiry_date: product.expiry_date || '',
            // Customer uniqueness constraint
            unique_per_customer: product.unique_per_customer || 'N',
            // Loan disbursement requirement
            required_for_loan_disbursement: product.required_for_loan_disbursement || 'N',
            // PHU allocation basis flag
            phu_basis: product.phu_basis || 'N',
            // Description fields
            description: product.description || '',
            eligibility_criteria: product.eligibility_criteria || '',
            benefits: product.benefits || ''
        };

        // Pre-format all numeric fields for display
        this.formData.interest_rate_formatted = this.formatDecimalForDisplay(this.formData.interest_rate);
        this.formData.minimum_balance_formatted = this.formatNumberForDisplay(this.formData.minimum_balance);
        this.formData.maximum_balance_formatted = this.formatNumberForDisplay(this.formData.maximum_balance);
        this.formData.minimum_initial_deposit_formatted = this.formatNumberForDisplay(this.formData.minimum_initial_deposit);
        this.formData.withdrawal_fee_formatted = this.formatNumberForDisplay(this.formData.withdrawal_fee);
        this.formData.deposit_fee_formatted = this.formatNumberForDisplay(this.formData.deposit_fee);
        this.formData.monthly_maintenance_fee_formatted = this.formatNumberForDisplay(this.formData.monthly_maintenance_fee);
        this.formData.inactive_fee_formatted = this.formatNumberForDisplay(this.formData.inactive_fee);

        this.errors = {};
        this.formElement = null;
        this.productId = product.id;

        // Capture vnode attributes
        this.onHide = vnode.attrs.onHide;
        this.onRefresh = vnode.attrs.onRefresh;
        this.subunits = vnode.attrs.subunits || [];

        debugComponentMount('EditSimpananForm', 'oninit completed');
    },

    oncreate: function (vnode) {
        debugComponentMount('EditSimpananForm', 'oncreate - DOM mounted');
        // Ensure form buttons are properly attached
        const formElement = vnode.dom.querySelector('form');
        const submitButton = vnode.dom.querySelector('button[type="submit"]');

        console.log('EditSimpananForm: Form element found:', !!formElement);
        console.log('EditSimpananForm: Submit button found:', !!submitButton);

        if (submitButton) {
            console.log('EditSimpananForm: Submit button state:', {
                disabled: submitButton.disabled,
                textContent: submitButton.textContent
            });
        }
    },

    handleInput: function (field, value) {
        this.formData[field] = value;
        // Clear error when user starts typing
        if (this.errors[field]) {
            delete this.errors[field];
        }
    },

    // Handle decimal input for interest rates (like PinjamanTab approach)
    handleDecimalInput: function (field, value) {
        this.formData[field] = value;

        // Clear error when user starts typing
        if (this.errors[field]) {
            delete this.errors[field];
        }
    },

    // Handle formatted input for number fields
    handleFormattedInput: function (field, value) {
        // Store the formatted value for display
        this.formData[field + '_formatted'] = value;

        // Parse and store the numeric value for submission
        const numericValue = this.parseFormattedNumber(value);
        this.formData[field] = numericValue;

        // Clear error when user starts typing
        if (this.errors[field]) {
            delete this.errors[field];
        }
    },

    // Format decimal number to 2 decimal places for display
    formatDecimalForDisplay: function (value) {
        if (value === '' || value === null || value === undefined) {
            return '';
        }

        const num = parseFloat(value);
        if (isNaN(num)) {
            return '';
        }

        return num.toFixed(2);
    },

    // Format number with thousand separators for display
    formatNumberForDisplay: function (value) {
        if (value === '' || value === null || value === undefined) {
            return '';
        }

        // Convert to string and remove non-numeric characters except decimal point and comma
        let cleanValue = value.toString().replace(/[^\d.,-]/g, '');

        // Handle negative numbers
        const isNegative = cleanValue.startsWith('-');
        if (isNegative) {
            cleanValue = cleanValue.substring(1);
        }

        // Handle decimal values (both . and , as decimal separators)
        const dotIndex = cleanValue.indexOf('.');
        const commaIndex = cleanValue.indexOf(',');

        // Determine which is the decimal separator (last one)
        let decimalSeparator = null;
        let decimalIndex = -1;

        if (dotIndex !== -1 && commaIndex !== -1) {
            // Both present, last one is decimal separator
            if (dotIndex > commaIndex) {
                decimalSeparator = '.';
                decimalIndex = dotIndex;
            } else {
                decimalSeparator = ',';
                decimalIndex = commaIndex;
            }
        } else if (dotIndex !== -1) {
            decimalSeparator = '.';
            decimalIndex = dotIndex;
        } else if (commaIndex !== -1) {
            decimalSeparator = ',';
            decimalIndex = commaIndex;
        }

        if (decimalIndex !== -1) {
            const integerPart = cleanValue.substring(0, decimalIndex);
            const decimalPart = cleanValue.substring(decimalIndex + 1).substring(0, 2); // Limit to 2 decimal places

            if (integerPart === '') {
                return (isNegative ? '-' : '') + '0' + decimalSeparator + decimalPart;
            }

            // Format integer part with thousand separators
            const formattedInteger = parseInt(integerPart, 10).toLocaleString('id-ID');
            return (isNegative ? '-' : '') + formattedInteger + decimalSeparator + decimalPart;
        } else {
            // Format integer values
            const num = parseInt(cleanValue, 10);
            if (isNaN(num)) {
                return '';
            }
            return (isNegative ? '-' : '') + num.toLocaleString('id-ID');
        }
    },

    // Parse formatted number back to float
    parseFormattedNumber: function (value) {
        if (value === '' || value === null || value === undefined) {
            return '';
        }

        // Normalize decimal separator (convert comma to dot for decimal)
        let normalizedValue = value.toString().replace(/[^\d.,-]/g, ''); // Remove non-numeric except ., and -

        // Handle negative numbers
        const isNegative = normalizedValue.startsWith('-');
        if (isNegative) {
            normalizedValue = normalizedValue.substring(1);
        }

        // Check if there's a clear decimal separator (dot or comma followed by 0-2 digits)
        const dotIndex = normalizedValue.lastIndexOf('.');
        const commaIndex = normalizedValue.lastIndexOf(',');

        let hasDecimal = false;
        let decimalSeparator = null;
        let decimalIndex = -1;

        // Check for dot as decimal separator (must have digits before and optionally after)
        if (dotIndex !== -1) {
            const beforeDot = normalizedValue.substring(0, dotIndex);
            const afterDot = normalizedValue.substring(dotIndex + 1);

            // If there are digits before the dot, and it's not clearly a thousand separator pattern
            if (beforeDot.length > 0 && /^\d+$/.test(beforeDot)) {
                // Check if this looks like a thousand separator (dot followed by exactly 3 digits, preceded by 3+ digits)
                const isThousandSeparator = afterDot.length === 3 && /^\d{3}$/.test(afterDot) &&
                    beforeDot.length >= 3 && /^\d{1,3}$/.test(beforeDot.slice(-3));

                if (!isThousandSeparator) {
                    hasDecimal = true;
                    decimalSeparator = '.';
                    decimalIndex = dotIndex;
                }
            }
        }

        // Check for comma as decimal separator if no dot decimal found
        if (!hasDecimal && commaIndex !== -1) {
            const beforeComma = normalizedValue.substring(0, commaIndex);
            const afterComma = normalizedValue.substring(commaIndex + 1);

            // If there are digits before the comma, and it's not clearly a thousand separator pattern
            if (beforeComma.length > 0 && /^\d+$/.test(beforeComma)) {
                // Similar logic for comma as thousand separator
                const isThousandSeparator = afterComma.length === 3 && /^\d{3}$/.test(afterComma) &&
                    beforeComma.length >= 3 && /^\d{1,3}$/.test(beforeComma.slice(-3));

                if (!isThousandSeparator) {
                    hasDecimal = true;
                    decimalSeparator = ',';
                    decimalIndex = commaIndex;
                }
            }
        }

        if (hasDecimal) {
            // Split at decimal separator
            let integerPart = normalizedValue.substring(0, decimalIndex);
            let decimalPart = normalizedValue.substring(decimalIndex + 1);

            // Remove thousand separators from integer part
            integerPart = integerPart.replace(/[.,]/g, '');

            // Reconstruct with dot as decimal separator
            normalizedValue = integerPart + '.' + decimalPart;
        } else {
            // No decimal separator found, treat all dots and commas as thousand separators
            normalizedValue = normalizedValue.replace(/[.,]/g, '');
        }

        const num = parseFloat((isNegative ? '-' : '') + normalizedValue);
        return isNaN(num) ? '' : num;
    },

    validateForm: function () {
        this.errors = {};
        let isValid = true;

        if (!this.formData.code.trim()) {
            this.errors.code = "Kode produk harus diisi";
            isValid = false;
        }

        if (!this.formData.name.trim()) {
            this.errors.name = "Nama produk harus diisi";
            isValid = false;
        }

        // Validate interest rate (should be between 0 and 99.99)
        const interestRate = parseFloat(this.formData.interest_rate) || 0;
        if (interestRate < 0 || interestRate > 99.99) {
            this.errors.interest_rate = "Suku Bunga harus antara 0% dan 99,99%";
            isValid = false;
        }

        // Validate balance limits
        const minBalance = parseFloat(this.formData.minimum_balance) || 0;
        const maxBalance = this.formData.maximum_balance !== '' && this.formData.maximum_balance !== null && this.formData.maximum_balance !== undefined ? parseFloat(this.formData.maximum_balance) : null;
        const minInitialDeposit = this.formData.minimum_initial_deposit !== '' && this.formData.minimum_initial_deposit !== null && this.formData.minimum_initial_deposit !== undefined ? parseFloat(this.formData.minimum_initial_deposit) : null;

        if (maxBalance !== null && maxBalance <= minBalance) {
            this.errors.maximum_balance = "Saldo Maximum harus lebih besar dari Saldo Minimum";
            isValid = false;
        }

        if (minInitialDeposit !== null && minInitialDeposit < minBalance) {
            this.errors.minimum_initial_deposit = "Setoran Awal Minimum tidak boleh kurang dari Saldo Minimum";
            isValid = false;
        }

        return isValid;
    },


    handleSubmit: function (e) {
        console.log('EditSimpananForm: handleSubmit called', { event: e, isSubmitting: this.isSubmitting });
        e.preventDefault();

        if (!this.validateForm()) {
            console.log('EditSimpananForm: Form validation failed');
            toast.show('Mohon perbaiki kesalahan pada form terlebih dahulu', 'warning');
            return;
        }

        console.log('EditSimpananForm: Form validation passed, starting submission');
        this.isSubmitting = true;
        // Force immediate UI update to show loading state
        m.redraw.sync();

        // Prepare all fields from schema for PUT request
        const submitData = {
            id: this.productId,
            code: this.formData.code,
            name: this.formData.name,
            subunit_id: this.formData.subunit_id,
            // Interest rate fields
            interest_rate: parseFloat(this.formData.interest_rate) || 0.00,
            interest_calculation_method: this.formData.interest_calculation_method || null,
            interest_payment_frequency: this.formData.interest_payment_frequency || null,
            // Balance limit fields
            minimum_balance: parseFloat(this.formData.minimum_balance) || 0.00,
            // Per-product opt-in for the subunit minimum-balance penalty (/umum preferensi)
            minimum_balance_enforced: this.formData.minimum_balance_enforced || 'N',
            maximum_balance: (this.formData.maximum_balance && parseFloat(this.formData.maximum_balance) > 0) ? parseFloat(this.formData.maximum_balance) : null,
            minimum_initial_deposit: (this.formData.minimum_initial_deposit === '' || this.formData.minimum_initial_deposit === null || this.formData.minimum_initial_deposit === undefined) ? null : parseFloat(this.formData.minimum_initial_deposit),
            // Transaction limit fields
            minimum_deposit_amount: this.formData.minimum_deposit_amount ? parseFloat(this.formData.minimum_deposit_amount) : null,
            maximum_deposit_amount: this.formData.maximum_deposit_amount ? parseFloat(this.formData.maximum_deposit_amount) : null,
            minimum_withdrawal_amount: this.formData.minimum_withdrawal_amount ? parseFloat(this.formData.minimum_withdrawal_amount) : null,
            maximum_daily_withdrawal_amount: this.formData.maximum_daily_withdrawal_amount ? parseFloat(this.formData.maximum_daily_withdrawal_amount) : null,
            maximum_daily_withdrawal_count: this.formData.maximum_daily_withdrawal_count ? parseInt(this.formData.maximum_daily_withdrawal_count) : null,
            // ATM specific limits
            atm_daily_limit_amount: this.formData.atm_daily_limit_amount ? parseFloat(this.formData.atm_daily_limit_amount) : null,
            atm_daily_limit_count: this.formData.atm_daily_limit_count ? parseInt(this.formData.atm_daily_limit_count) : null,
            atm_transaction_limit: this.formData.atm_transaction_limit ? parseFloat(this.formData.atm_transaction_limit) : null,
            // Fee fields
            monthly_maintenance_fee: parseFloat(this.formData.monthly_maintenance_fee) || 0.00,
            withdrawal_fee: parseFloat(this.formData.withdrawal_fee) || 0.00,
            deposit_fee: parseFloat(this.formData.deposit_fee) || 0.00,
            inactive_fee: parseFloat(this.formData.inactive_fee) || 0.00,
            // Status and activation fields
            is_active: this.formData.is_active || 'Y',
            activation_date: this.formData.activation_date ? new Date(this.formData.activation_date).toISOString() : null,
            expiry_date: this.formData.expiry_date ? new Date(this.formData.expiry_date).toISOString() : null,
            // Customer uniqueness constraint
            unique_per_customer: this.formData.unique_per_customer || 'N',
            // Loan disbursement requirement
            required_for_loan_disbursement: this.formData.required_for_loan_disbursement || 'N',
            // PHU allocation basis flag
            phu_basis: this.formData.phu_basis || 'N',
            // Description fields
            description: this.formData.description || null,
            eligibility_criteria: this.formData.eligibility_criteria || null,
            benefits: this.formData.benefits || null
        };

        console.log('EditSimpananForm: Submitting data to API:', submitData);

        // Submit form data using enhanced Mithril utility with improved error handling
        putMithril("/api/saving-products", submitData, {
            component: this,
            showLoading: true,
            showErrorToast: false // Handle errors manually for field-specific cases
        }).then(response => {
            console.log('EditSimpananForm: API response received:', response);
            this.isSubmitting = false;
            m.redraw();

            if (response.success) {
                console.log('EditSimpananForm: Update successful');
                toast.show("Produk simpanan berhasil diperbarui", "success");
                this.onRefresh();
                this.onHide();
            } else {
                console.error('EditSimpananForm: Update failed:', response.error);
                // Handle specific error types that need field-focus
                if (response.error && typeof response.error === 'string' && response.error.includes('sudah digunakan')) {
                    // Duplicate code error - set field-specific error and show toast
                    this.errors.code = response.error;
                    toast.show(response.error, "error");
                    console.log('EditSimpananForm: Showing duplicate code error toast');
                    // Focus on the code field to help user fix the issue
                    setTimeout(() => {
                        const codeField = document.querySelector('input[id="code"]');
                        if (codeField) {
                            codeField.focus();
                            codeField.select();
                        }
                    }, 100);
                } else {
                    // For other errors, let makeMithrilRequest handle them
                    throw new Error(response.error || "Gagal memperbarui produk simpanan");
                }
            }
        }).catch(error => {
            console.error('EditSimpananForm: Error in submission:', error);
            this.isSubmitting = false;
            m.redraw.sync();

            // Check for duplicate code error in various formats
            const isDuplicateCodeError =
                (error.message && error.message.includes('sudah digunakan')) ||
                (error.error && typeof error.error === 'string' && error.error.includes('sudah digunakan')) ||
                (error.status === 409);

            if (isDuplicateCodeError) {
                const duplicateErrorMessage = error.message || error.error || "Kode produk sudah digunakan. Silakan gunakan kode yang berbeda.";
                this.errors.code = duplicateErrorMessage;

                // Show toast notification
                showErrorToast(duplicateErrorMessage);

                // Focus on code field for user to fix
                setTimeout(() => {
                    const codeField = document.querySelector('input[id="code"]');
                    if (codeField) {
                        codeField.focus();
                        codeField.select();
                    }
                }, 100);
            } else {
                // For all other errors, let makeMithrilRequest handle them
                // The utility will show appropriate error toasts and handle redirects
                this.errors.general = error.message || "Terjadi kesalahan saat memperbarui produk";
                showErrorToast(this.errors.general);
            }
        });
    },

    view: function (vnode) {
        const { product, onHide } = vnode.attrs;
        const subunits = vnode.attrs.subunits || [
            { id: 1, nama: 'Test Subunit 1' },
            { id: 2, nama: 'Test Subunit 2' }
        ];

        // Form input class constant
        const inputClassBase = "mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-orange-500 focus:ring-orange-500 dark:bg-gray-700 dark:text-white";
        const labelClass = "block text-sm font-medium text-gray-700 dark:text-gray-300";

        // Create a backdrop and modal structure for the edit form
        return m("div", {
            class: "fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50",
            onclick: () => onHide() // Close modal when clicking outside the form
        }, [
            m("div", {
                class: "bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-screen overflow-y-auto",
                onclick: (e) => e.stopPropagation() // Prevent click inside modal from closing it
            }, [
                m("div", { class: "bg-white dark:bg-gray-800 px-6 py-4 border-b border-gray-200 dark:border-gray-700 rounded-t-lg" }, [
                    m("h3", { class: "text-lg font-medium text-gray-900 dark:text-white" }, "Edit Produk Simpanan"),
                    m("p", { class: "mt-1 text-sm text-gray-600 dark:text-gray-400" }, "Perbarui informasi produk simpanan")
                ]),
                // Remove the duplicate subunit fields here - they were located between the header and the form

                m("form", { onsubmit: (e) => this.handleSubmit(e) }, [
                    m("div", { class: "px-6 py-4 space-y-4" }, [
                        // Subunit field moved here from outside the form
                        m("div", { class: "px-6 py-4 space-y-6" }, [
                            // Code field
                            m("div", [
                                m("label", {
                                    class: labelClass,
                                    for: "code"
                                }, "Kode Produk"),
                                m("input", {
                                    type: "text",
                                    id: "code",
                                    class: inputClassBase,
                                    value: this.formData.code,
                                    oninput: e => this.handleInput('code', e.target.value)
                                }),
                                this.errors.code && m("p", { class: "mt-1 text-sm text-red-600 dark:text-red-400" }, this.errors.code)
                            ]),

                            // Product Type field
                            m("div", [
                                m("label", { class: labelClass }, "Jenis Produk"),
                                m("div", { class: "flex items-center space-x-4 mt-2" }, [
                                    m("label", { class: "inline-flex items-center cursor-pointer" }, [
                                        m("input", {
                                            type: "radio",
                                            name: "product_type_edit",
                                            value: "Biasa",
                                            checked: this.formData.product_type === 'Biasa',
                                            onchange: e => this.handleInput('product_type', e.target.value),
                                            class: "form-radio h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300"
                                        }),
                                        m("span", { class: "ml-2 text-sm text-gray-700 dark:text-gray-300" }, "Biasa")
                                    ]),
                                    m("label", { class: "inline-flex items-center cursor-pointer" }, [
                                        m("input", {
                                            type: "radio",
                                            name: "product_type_edit",
                                            value: "Berjangka",
                                            checked: this.formData.product_type === 'Berjangka',
                                            onchange: e => this.handleInput('product_type', e.target.value),
                                            class: "form-radio h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300"
                                        }),
                                        m("span", { class: "ml-2 text-sm text-gray-700 dark:text-gray-300" }, "Berjangka")
                                    ])
                                ])
                            ]),

                            // Name field
                            m("div", [
                                m("label", {
                                    class: labelClass,
                                    for: "name"
                                }, "Nama Produk"),
                                m("input", {
                                    type: "text",
                                    id: "name",
                                    class: inputClassBase,
                                    value: this.formData.name,
                                    oninput: e => this.handleInput('name', e.target.value)
                                }),
                                this.errors.name && m("p", { class: "mt-1 text-sm text-red-600 dark:text-red-400" }, this.errors.name)
                            ]),

                            // Subunit field - replaced with disabled input showing current user's subunit
                            m("div", [
                                m("label", {
                                    class: labelClass,
                                    for: "subunit"
                                }, getTerm('tableHeaders.subunit')),
                                m("input", {
                                    type: "text",
                                    id: "subunit",
                                    class: inputClassBase,
                                    value: (() => {
                                        if (!product || !product.subunit_id) {
                                            return 'Subunit tidak ditentukan';
                                        }

                                        const currentSubunit = this.subunits.find(s => s.id == product.subunit_id);
                                        return currentSubunit ? currentSubunit.nama : `Subunit ID: ${product.subunit_id} (nama tidak ditemukan)`;
                                    })(),
                                    disabled: true
                                }),
                                m("p", { class: "mt-1 text-sm text-gray-500 dark:text-gray-400" }, "Subunit ditentukan berdasarkan pengguna saat ini")
                            ]),

                            // Interest rate section
                            m("div", { class: "space-y-4 bg-gray-50 dark:bg-gray-700 p-4 rounded-lg" }, [
                                m("h4", { class: "text-md font-semibold text-gray-800 dark:text-white" }, "Pengaturan Bunga"),
                                m("div", [
                                    m("label", {
                                        class: labelClass,
                                        for: "interest_rate"
                                    }, "Suku Bunga (% per tahun)"),
                                    m("input", {
                                        type: "number",
                                        id: "interest_rate",
                                        step: "0.01",
                                        min: "0",
                                        max: "99.99",
                                        class: inputClassBase,
                                        placeholder: "0.00",
                                        value: this.formData.interest_rate,
                                        oninput: e => this.handleDecimalInput('interest_rate', e.target.value)
                                    }),
                                    this.errors.interest_rate && m("p", { class: "mt-1 text-sm text-red-600 dark:text-red-400" }, this.errors.interest_rate)
                                ]),
                                m("div", [
                                    m("label", {
                                        class: labelClass,
                                        for: "interest_calculation_method"
                                    }, "Metode Perhitungan Bunga"),
                                    m("select", {
                                        id: "interest_calculation_method",
                                        class: inputClassBase,
                                        value: this.formData.interest_calculation_method || "daily_balance",
                                        onchange: e => {
                                            this.formData.interest_calculation_method = e.target.value;
                                            this.formData.interest_payment_frequency = 'monthly';
                                        }
                                    }, [
                                        m("option", { value: "daily_balance" }, "Metode Saldo Harian"),
                                        m("option", { value: "lowest_balance" }, "Metode Saldo Terendah"),
                                        m("option", { value: "average_balance" }, "Metode Saldo Rata-rata")
                                    ])
                                ])
                            ]),

                            // Balance limits section
                            m("div", { class: "space-y-4 bg-gray-50 dark:bg-gray-700 p-4 rounded-lg" }, [
                                m("h4", { class: "text-md font-semibold text-gray-800 dark:text-white" }, "Batas Saldo"),
                                m("div", [
                                    m("label", {
                                        class: labelClass,
                                        for: "minimum_balance"
                                    }, "Saldo Minimum (Rp)"),
                                    m("input", {
                                        type: "text",
                                        id: "minimum_balance",
                                        class: inputClassBase,
                                        placeholder: "0",
                                        value: this.formData.minimum_balance_formatted || this.formData.minimum_balance || '',
                                        oninput: e => this.handleFormattedInput('minimum_balance', e.target.value),
                                        onblur: e => {
                                            // Format the value when user leaves the field
                                            const formatted = this.formatNumberForDisplay(this.formData.minimum_balance);
                                            this.formData.minimum_balance_formatted = formatted;
                                            e.target.value = formatted;
                                        }
                                    })
                                ]),
                                m("div", [
                                    m("label", {
                                        class: labelClass,
                                        for: "maximum_balance"
                                    }, "Saldo Maximum (Rp)"),
                                    m("input", {
                                        type: "text",
                                        id: "maximum_balance",
                                        class: inputClassBase,
                                        placeholder: "Kosongkan untuk unlimited",
                                        value: this.formData.maximum_balance_formatted || this.formData.maximum_balance || '',
                                        oninput: e => this.handleFormattedInput('maximum_balance', e.target.value),
                                        onblur: e => {
                                            // Format the value when user leaves the field
                                            const formatted = this.formatNumberForDisplay(this.formData.maximum_balance);
                                            this.formData.maximum_balance_formatted = formatted;
                                            e.target.value = formatted;
                                        }
                                    }),
                                    m("p", { class: "mt-1 text-sm text-gray-500 dark:text-gray-400" }, "Kosongkan atau isi 0 untuk unlimited"),
                                    this.errors.maximum_balance && m("p", { class: "mt-1 text-sm text-red-600 dark:text-red-400" }, this.errors.maximum_balance)
                                ]),
                                m("div", [
                                    m("label", {
                                        class: labelClass,
                                        for: "minimum_initial_deposit"
                                    }, "Setoran Awal Minimum (Rp)"),
                                    m("input", {
                                        type: "text",
                                        id: "minimum_initial_deposit",
                                        class: inputClassBase,
                                        placeholder: "0",
                                        value: this.formData.minimum_initial_deposit_formatted || this.formData.minimum_initial_deposit || '',
                                        oninput: e => this.handleFormattedInput('minimum_initial_deposit', e.target.value),
                                        onblur: e => {
                                            // Format the value when user leaves the field
                                            const formatted = this.formatNumberForDisplay(this.formData.minimum_initial_deposit);
                                            this.formData.minimum_initial_deposit_formatted = formatted;
                                            e.target.value = formatted;
                                        }
                                    }),
                                    this.errors.minimum_initial_deposit && m("p", { class: "mt-1 text-sm text-red-600 dark:text-red-400" }, this.errors.minimum_initial_deposit)
                                ]),
                                renderMinimumBalanceEnforcedField(labelClass, this)
                            ]),

                            // Fee Configuration section
                            m("div", { class: "space-y-4 bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-700" }, [
                                m("h4", { class: "text-md font-semibold text-gray-800 dark:text-white" }, "Konfigurasi Biaya"),
                                m("div", [
                                    m("label", {
                                        class: labelClass,
                                        for: "withdrawal_fee"
                                    }, "Biaya Penarikan (Rp)"),
                                    m("input", {
                                        type: "text",
                                        id: "withdrawal_fee",
                                        class: inputClassBase,
                                        placeholder: "0",
                                        value: this.formData.withdrawal_fee_formatted || this.formData.withdrawal_fee || '',
                                        oninput: e => this.handleFormattedInput('withdrawal_fee', e.target.value),
                                        onblur: e => {
                                            // Format the value when user leaves the field
                                            const formatted = this.formatNumberForDisplay(this.formData.withdrawal_fee);
                                            this.formData.withdrawal_fee_formatted = formatted;
                                            e.target.value = formatted;
                                        }
                                    }),
                                    m("p", { class: "mt-1 text-sm text-gray-500 dark:text-gray-400" }, "Biaya yang dikenakan saat melakukan penarikan")
                                ]),
                                m("div", [
                                    m("label", {
                                        class: labelClass,
                                        for: "deposit_fee"
                                    }, "Biaya Setoran (Rp)"),
                                    m("input", {
                                        type: "text",
                                        id: "deposit_fee",
                                        class: inputClassBase,
                                        placeholder: "0",
                                        value: this.formData.deposit_fee_formatted || this.formData.deposit_fee || '',
                                        oninput: e => this.handleFormattedInput('deposit_fee', e.target.value),
                                        onblur: e => {
                                            // Format the value when user leaves the field
                                            const formatted = this.formatNumberForDisplay(this.formData.deposit_fee);
                                            this.formData.deposit_fee_formatted = formatted;
                                            e.target.value = formatted;
                                        }
                                    }),
                                    m("p", { class: "mt-1 text-sm text-gray-500 dark:text-gray-400" }, "Biaya yang dikenakan saat melakukan setoran")
                                ]),
                                m("div", [
                                    m("label", {
                                        class: labelClass,
                                        for: "monthly_maintenance_fee"
                                    }, "Biaya Administrasi Bulanan (Rp)"),
                                    m("input", {
                                        type: "text",
                                        id: "monthly_maintenance_fee",
                                        class: inputClassBase,
                                        placeholder: "0",
                                        value: this.formData.monthly_maintenance_fee_formatted || this.formData.monthly_maintenance_fee || '',
                                        oninput: e => this.handleFormattedInput('monthly_maintenance_fee', e.target.value),
                                        onblur: e => {
                                            // Format the value when user leaves the field
                                            const formatted = this.formatNumberForDisplay(this.formData.monthly_maintenance_fee);
                                            this.formData.monthly_maintenance_fee_formatted = formatted;
                                            e.target.value = formatted;
                                        }
                                    }),
                                    m("p", { class: "mt-1 text-sm text-gray-500 dark:text-gray-400" }, "Biaya administrasi yang dikenakan setiap bulan")
                                ]),
                                m("div", [
                                    m("label", {
                                        class: labelClass,
                                        for: "inactive_fee"
                                    }, "Biaya Tidak Aktif (Rp)"),
                                    m("input", {
                                        type: "text",
                                        id: "inactive_fee",
                                        class: inputClassBase,
                                        placeholder: "0",
                                        value: this.formData.inactive_fee_formatted || this.formData.inactive_fee || '',
                                        oninput: e => this.handleFormattedInput('inactive_fee', e.target.value),
                                        onblur: e => {
                                            // Format the value when user leaves the field
                                            const formatted = this.formatNumberForDisplay(this.formData.inactive_fee);
                                            this.formData.inactive_fee_formatted = formatted;
                                            e.target.value = formatted;
                                        }
                                    }),
                                    m("p", { class: "mt-1 text-sm text-gray-500 dark:text-gray-400" }, "Biaya yang dikenakan untuk rekening yang tidak aktif")
                                ])
                            ]),

                            // Status and Description section
                            m("div", { class: "space-y-4 bg-gray-50 dark:bg-gray-700 p-4 rounded-lg" }, [
                                m("h4", { class: "text-md font-semibold text-gray-800 dark:text-white" }, "Status & Deskripsi"),
                                m("div", [
                                    m("label", {
                                        class: labelClass,
                                        for: "is_active"
                                    }, "Status Produk"),
                                    m("select", {
                                        id: "is_active",
                                        class: inputClassBase,
                                        value: this.formData.is_active,
                                        oninput: e => this.handleInput('is_active', e.target.value)
                                    }, [
                                        m("option", { value: "Y" }, "Aktif"),
                                        m("option", { value: "N" }, "Tidak Aktif")
                                    ])
                                ]),
                                m("div", [
                                    m("label", {
                                        class: labelClass,
                                        for: "unique_per_customer"
                                    }, "Unik per Nasabah"),
                                    m("select", {
                                        id: "unique_per_customer",
                                        class: inputClassBase,
                                        value: this.formData.unique_per_customer,
                                        oninput: e => this.handleInput('unique_per_customer', e.target.value)
                                    }, [
                                        m("option", { value: "N" }, "Tidak"),
                                        m("option", { value: "Y" }, "Ya")
                                    ]),
                                    m("p", { class: "mt-1 text-sm text-gray-500 dark:text-gray-400" }, "Jika Ya, setiap nasabah hanya boleh memiliki satu rekening untuk produk ini")
                                ]),
                                m("div", [
                                    m("label", {
                                        class: labelClass,
                                        for: "required_for_loan_disbursement"
                                    }, "Wajib untuk Pencairan Pinjaman"),
                                    m("select", {
                                        id: "required_for_loan_disbursement",
                                        class: inputClassBase,
                                        value: this.formData.required_for_loan_disbursement,
                                        oninput: e => this.handleInput('required_for_loan_disbursement', e.target.value)
                                    }, [
                                        m("option", { value: "N" }, "Tidak"),
                                        m("option", { value: "Y" }, "Ya")
                                    ]),
                                    m("p", { class: "mt-1 text-sm text-gray-500 dark:text-gray-400" }, "Jika Ya, nasabah harus memiliki rekening aktif untuk produk ini sebelum dapat mencairkan pinjaman")
                                ]),
                                m("div", [
                                    m("label", {
                                        class: labelClass,
                                        for: "phu_basis"
                                    }, "Menjadi dasar perhitungan PHU"),
                                    m("select", {
                                        id: "phu_basis",
                                        class: inputClassBase,
                                        value: this.formData.phu_basis,
                                        oninput: e => this.handleInput('phu_basis', e.target.value)
                                    }, [
                                        m("option", { value: "N" }, "Tidak"),
                                        m("option", { value: "Y" }, "Ya")
                                    ]),
                                    m("p", { class: "mt-1 text-sm text-gray-500 dark:text-gray-400" }, "Jika Ya, saldo dan mutasi produk simpanan ini akan dihitung sebagai basis alokasi PHU anggota")
                                ]),
                                m("div", [
                                    m("label", {
                                        class: labelClass,
                                        for: "description"
                                    }, "Deskripsi"),
                                    m("textarea", {
                                        id: "description",
                                        rows: "3",
                                        class: inputClassBase,
                                        placeholder: "Deskripsi produk simpanan",
                                        value: this.formData.description,
                                        oninput: e => this.handleInput('description', e.target.value)
                                    })
                                ])
                            ])
                        ]),

                        m("div", { class: "bg-gray-50 dark:bg-gray-700 px-6 py-3 rounded-b-lg flex justify-end space-x-3" }, [
                            m("button", {
                                type: "button",
                                class: "inline-flex justify-center py-2 px-4 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-600 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500",
                                onclick: onHide
                            }, "Batal"),
                            m("button", {
                                type: "submit",
                                class: "inline-flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed",
                                disabled: this.isSubmitting,
                                onclick: (e) => {
                                    console.log('EditSimpananForm: Submit button clicked', { event: e, isSubmitting: this.isSubmitting });
                                }
                            }, [
                                this.isSubmitting && m("svg", {
                                    class: "animate-spin -ml-1 mr-2 h-4 w-4 text-white",
                                    fill: "none",
                                    viewBox: "0 0 24 24"
                                }, [
                                    m("circle", {
                                        class: "opacity-25",
                                        cx: "12",
                                        cy: "12",
                                        r: "10",
                                        stroke: "currentColor",
                                        "stroke-width": "4"
                                    }),
                                    m("path", {
                                        class: "opacity-75",
                                        fill: "currentColor",
                                        d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                    })
                                ]),
                                this.isSubmitting ? "Memperbarui..." : "Perbarui"
                            ])
                        ])
                    ])
                ])])])
    }
}

export default SimpananTab;
